import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';

const NovuSubscriberManagerPage = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [formData, setFormData] = useState({
        subscriber_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        avatar: '',
        timezone: '',
        locale: '',
        data: {}, // Custom data
        entity_type: 'patient', // 'patient', 'provider', 'user'
        entity_id: ''
    });
    const [editingSubscriberId, setEditingSubscriberId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('create'); // 'create' or 'edit'
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    
    // New state for entity management
    const [availableEntities, setAvailableEntities] = useState([]);
    const [entitySearch, setEntitySearch] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [entityTypeFilter, setEntityTypeFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({});

    useEffect(() => {
        fetchSubscribers(currentPage, searchFilter, entityTypeFilter);
    }, [currentPage, searchFilter, entityTypeFilter]);

    const fetchSubscribers = async (page = 1, search = '', entityType = '') => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
            if (search.trim()) params.append('search', search.trim());
            if (entityType.trim()) params.append('entity_type', entityType.trim());
            
            const response = await adminApi.get(`/admin/novu/subscribers/local/?${params}`);
            setSubscribers(response.data.subscribers || []);
            setPagination(response.data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error("Failed to fetch local NovuSubscriber records:", err);
            setError(`Failed to fetch NovuSubscriber records: ${err.response?.data?.detail || err.message}`);
            setSubscribers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'data') {
            try {
                setFormData({ ...formData, [name]: JSON.parse(value) });
            } catch (jsonError) {
                // Handle invalid JSON input
                console.error("Invalid JSON for custom data:", jsonError);
                // Optionally, show an error message to the user
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            if (modalType === 'create') {
                await adminApi.post('/admin/novu/subscribers/local/', formData);
                setMessage({ type: 'success', text: 'NovuSubscriber created successfully!' });
            } else { // modalType === 'edit'
                await adminApi.put(`/admin/novu/subscribers/local/${editingSubscriberId}/`, formData);
                setMessage({ type: 'success', text: 'NovuSubscriber updated successfully!' });
            }
            setShowModal(false);
            resetForm();
            fetchSubscribers(currentPage, searchFilter, entityTypeFilter); // Refresh list
        } catch (err) {
            console.error("Failed to save NovuSubscriber:", err);
            setError(`Failed to save NovuSubscriber: ${err.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = async (subscriberId) => {
        setEditingSubscriberId(subscriberId);
        setModalType('edit');
        setError(null);
        setLoading(true);
        try {
            const response = await adminApi.get(`/admin/novu/subscribers/local/${subscriberId}/`);
            const subscriberData = response.data;
            // Map API response fields to form fields
            // API returns entity_email, entity_phone, entity_name but form uses email, phone, first_name, last_name
            setFormData({
                subscriber_id: subscriberData.subscriber_id || '',
                first_name: subscriberData.first_name || subscriberData.entity_name?.split(' ')[0] || '',
                last_name: subscriberData.last_name || subscriberData.entity_name?.split(' ').slice(1).join(' ') || '',
                email: subscriberData.email || subscriberData.entity_email || '',
                phone: subscriberData.phone || subscriberData.entity_phone || '',
                avatar: subscriberData.avatar || '',
                timezone: subscriberData.timezone || '',
                locale: subscriberData.locale || '',
                data: subscriberData.data || {},
                entity_type: subscriberData.entity_type || 'patient',
                entity_id: subscriberData.entity_id || ''
            });
            setShowModal(true);
        } catch (err) {
            console.error("Failed to fetch NovuSubscriber for edit:", err);
            setError(`Failed to fetch NovuSubscriber for edit: ${err.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (subscriberId) => {
        setDeleteConfirmId(subscriberId);
        setShowDeleteConfirmModal(true);
    };

    const confirmDelete = async () => {
        setError(null);
        setLoading(true);
        try {
            await adminApi.delete(`/admin/novu/subscribers/local/${deleteConfirmId}/`);
            setMessage({ type: 'success', text: 'NovuSubscriber deleted successfully!' });
            fetchSubscribers(currentPage, searchFilter, entityTypeFilter); // Refresh list
            setShowDeleteConfirmModal(false);
            setDeleteConfirmId(null);
        } catch (err) {
            console.error("Failed to delete NovuSubscriber:", err);
            setError(`Failed to delete NovuSubscriber: ${err.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            subscriber_id: '',
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            avatar: '',
            timezone: '',
            locale: '',
            data: {},
            entity_type: 'patient',
            entity_id: ''
        });
        setEditingSubscriberId(null);
        setModalType('create');
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    return (
        <>
        {/* Page Title */}
        <div className="row">
            <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 className="mb-sm-0 font-size-18">Novu Subscriber Management</h4>
                </div>
            </div>
        </div>

        {/* Action buttons */}
        <div className="d-flex justify-content-end align-items-center mb-4">
            <button type="button" className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                <i className="fa fa-plus me-1"></i> Add New Subscriber
            </button>
        </div>

        {loading && <div className="alert alert-info">Loading subscribers...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {subscribers.length === 0 ? (
            <div className="card">
                <div className="card-body">
                    <div className="alert text-secondary text-center mb-0">No NovuSubscriber records found.</div>
                </div>
            </div>
        ) : (
            <div className="card">
                <div className="card-body">
                    <AgGridTable
                            rowData={subscribers}
                            columnDefs={[
                                {
                                    headerName: 'Subscriber ID',
                                    field: 'subscriber_id',
                                    width: 180
                                },
                                {
                                    headerName: 'Entity Type',
                                    field: 'entity_type',
                                    width: 120,
                                    cellRenderer: (params) => {
                                        if (!params.data) return null;
                                        const type = params.data.entity_type;
                                        const bgClass = type === 'patient' ? 'primary' : type === 'provider' ? 'success' : 'info';
                                        return <span className={`badge bg-${bgClass}`}>{type}</span>;
                                    }
                                },
                                {
                                    headerName: 'Name',
                                    field: 'entity_name',
                                    width: 150,
                                    valueGetter: (p) => p.data?.entity_name || ''
                                },
                                {
                                    headerName: 'Email',
                                    field: 'entity_email',
                                    width: 200
                                },
                                {
                                    headerName: 'Phone',
                                    field: 'entity_phone',
                                    width: 140
                                },
                                {
                                    headerName: 'Created',
                                    field: 'created_at',
                                    width: 120,
                                    valueGetter: (p) => p.data?.created_at ? new Date(p.data.created_at).toLocaleDateString() : ''
                                },
                                {
                                    headerName: 'Actions',
                                    field: 'actions',
                                    width: 150,
                                    sortable: false,
                                    filter: false,
                                    cellRenderer: (params) => {
                                        if (!params.data) return null;
                                        return (
                                            <div className="btn-group btn-group-sm" role="group">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-info"
                                                    onClick={() => handleEditClick(params.data.subscriber_id)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDeleteClick(params.data.subscriber_id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        );
                                    }
                                }
                            ]}
                            defaultColDef={{
                                flex: 1,
                                minWidth: 100,
                                resizable: true,
                                sortable: true,
                                filter: true,
                            }}
                            gridOptions={{
                                autoSizeStrategy: { type: 'fitGridWidth' }
                            }}
                            getRowId={(params) => `${params.data.subscriber_id}`}
                        />
                </div>
            </div>
        )}

        {/* Create/Edit Subscriber Sidebar */}
        <SidebarPanel
                isOpen={showModal}
                onClose={handleCloseModal}
                title={modalType === 'create' ? 'Add New Subscriber' : `Edit Subscriber: ${formData.first_name} ${formData.last_name}`.trim()}
            >
                <form onSubmit={handleFormSubmit}>
                    <div className="mb-3">
                        <label htmlFor="formSubscriberId" className="form-label">Subscriber ID *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="formSubscriberId"
                            name="subscriber_id"
                            value={formData.subscriber_id}
                            onChange={handleInputChange}
                            required
                            disabled={modalType === 'edit'}
                        />
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="formFirstName" className="form-label">First Name</label>
                            <input
                                type="text"
                                className="form-control"
                                id="formFirstName"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="formLastName" className="form-label">Last Name</label>
                            <input
                                type="text"
                                className="form-control"
                                id="formLastName"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="formEmail" className="form-label">Email</label>
                            <input
                                type="email"
                                className="form-control"
                                id="formEmail"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="formPhone" className="form-label">Phone</label>
                            <input
                                type="text"
                                className="form-control"
                                id="formPhone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label htmlFor="formAvatar" className="form-label">Avatar URL</label>
                        <input
                            type="text"
                            className="form-control"
                            id="formAvatar"
                            name="avatar"
                            value={formData.avatar}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="row mb-3">
                        <div className="col-6">
                            <label htmlFor="formTimezone" className="form-label">Timezone</label>
                            <input
                                type="text"
                                className="form-control"
                                id="formTimezone"
                                name="timezone"
                                value={formData.timezone}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="col-6">
                            <label htmlFor="formLocale" className="form-label">Locale</label>
                            <input
                                type="text"
                                className="form-control"
                                id="formLocale"
                                name="locale"
                                value={formData.locale}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    <div className="mb-3">
                        <label htmlFor="formData" className="form-label">Custom Data (JSON)</label>
                        <textarea
                            className="form-control"
                            id="formData"
                            rows="3"
                            name="data"
                            value={JSON.stringify(formData.data, null, 2)}
                            onChange={handleInputChange}
                        ></textarea>
                        <div className="form-text text-muted">
                            Enter custom data as a valid JSON object.
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Subscriber'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                            Cancel
                        </button>
                    </div>
                </form>
            </SidebarPanel>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowDeleteConfirmModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                Are you sure you want to delete subscriber <strong>{deleteConfirmId}</strong>? This action cannot be undone.
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirmModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={loading}>
                                    {loading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NovuSubscriberManagerPage;